import React from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import useFetch from '../../../../hooks/useFetch';
import { API } from '../../../../utils/apiRoutes';

const StyledChip = styled(Chip)(({ theme, $available }) => ({
    margin: theme.spacing(0.5),
    cursor: $available ? 'pointer' : 'default',
    opacity: $available ? 1 : 0.6,
    '&:hover': {
        backgroundColor: $available ? theme.palette.primary.light : 'inherit',
        transform: $available ? 'scale(1.05)' : 'none',
    },
    transition: 'all 0.2s ease-in-out',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    gap: theme.spacing(1),
}));

const DaysContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
}));

const DaysContainerWrapper = styled(Box)(({ theme }) => ({
    minHeight: theme.spacing(10),
    padding: theme.spacing(1),
}));

    const DAY_NAMES = [
        'شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'
    ];

export default function DayTags({ 
    studentId, 
    teacherCourseId, 
    selectedDay,
    onDaySelect,
    enabled = false
}) {
    // Build query parameters
    const queryParams = React.useMemo(() => {
        const params = new URLSearchParams();
        
        if (teacherCourseId) {
            params.append('teacher_course_id', teacherCourseId.toString());
        }
        
        if (studentId) {
            params.append('student_id', studentId.toString());
        }
        
        return params;
    }, [teacherCourseId, studentId]);
    
    // Only fetch when enabled and teacher course is present
    const shouldFetch = enabled && Boolean(teacherCourseId);
    const url = shouldFetch ? `${API.availableSlots}?${queryParams.toString()}` : null;
    
    const { data, loading, error } = useFetch(url);
    
    const handleDayClick = (dayValue) => {
        if (onDaySelect) {
            onDaySelect(dayValue);
        }
    };
    
    // Always render wrapper, show helper text when not enabled
    if (!enabled) {
        return (
            <DaysContainerWrapper>
                <Typography align="center" variant="body2" color="text.secondary">
                    ابتدا هنرجو و دوره را انتخاب کنید
                </Typography>
            </DaysContainerWrapper>
        );
    }
    
    // Loading state
    if (loading) {
        return (
            <DaysContainerWrapper>
                <Typography align="center" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    در حال بارگذاری روزهای موجود...
                </Typography>
                <LoadingContainer>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                        بارگذاری...
                    </Typography>
                </LoadingContainer>
            </DaysContainerWrapper>
        );
    }
    
    // Error state
    if (error) {
        return (
            <DaysContainerWrapper>
                <Typography align="center" variant="body2" color="error">
                    خطا در بارگذاری روزهای موجود: {error}
                </Typography>
            </DaysContainerWrapper>
        );
    }
    
    // No data or empty days
    if (!data || !data.available_days || data.available_days.length === 0) {
        return (
            <DaysContainerWrapper>
                <Typography align="center" variant="body2" color="text.secondary">
                    روزهای موجودی برای این دوره یافت نشد.
                </Typography>
            </DaysContainerWrapper>
        );
    }
    
    const { available_days, capacity_full } = data;
    const isCapacityFull = capacity_full === true;
    
    // Create a set of available days for quick lookup
    const availableDaysSet = new Set(available_days.map(day => day.day));
    
    return (
        <DaysContainerWrapper>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                روزهای موجود:
            </Typography>
            
                        <DaysContainer>
                {DAY_NAMES.map((dayName, dayIndex) => {
                    const dayInfo = available_days.find(d => d.day === dayIndex);                                                                               
                    const isOpen = dayInfo ? dayInfo.is_open : false;
                    const isAvailable = isOpen && availableDaysSet.has(dayIndex);                                                                               
                    // Normalize comparison: convert both to numbers for consistent comparison
                    // Important: Check if selectedDay is empty/null/undefined first to avoid Number('') === 0 bug
                    const isSelected = selectedDay !== '' && selectedDay !== null && selectedDay !== undefined && Number(selectedDay) === dayIndex;
                    const slotCount = dayInfo ? dayInfo.slot_count : 0;
                    const chipLabel = dayName;
                    
                    // If day is selected, always show it as available for editing (even if not in available_days)
                    const shouldBeEnabled = isOpen || isSelected;
                    
                    let tooltipText;
                    if (isSelected) {
                        tooltipText = `انتخاب شده: ${dayName}`;
                    } else if (isOpen) {
                        tooltipText = `انتخاب ${dayName}`;
                    } else {
                        tooltipText = `${dayName} - آموزگار در این روز کار نمی‌کند`;                                                                             
                    }
                    
                    return (
                        <StyledChip
                            key={dayIndex}
                            label={chipLabel}
                            variant={isSelected ? "filled" : "outlined"}
                            color={isSelected ? "primary" : (isOpen ? "primary" : "default")}                                                                   
                            $available={shouldBeEnabled}
                            onClick={() => shouldBeEnabled && handleDayClick(dayIndex)}
                            disabled={!shouldBeEnabled}
                            title={tooltipText}
                        />
                    );
                })}
            </DaysContainer>
        </DaysContainerWrapper>
    );
}